import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/theming/material/material.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  RagVsAgenticComparisonData,
  RagVsAgenticEntry,
  RagVsAgenticTrendData,
  SolutionMetrics,
  ComparisonMetrics,
  RatingDistribution,
} from '../../../../shared/models/rag-vs-agentic.model';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

@Component({
  selector: 'app-rag-vs-agentic-widget',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    MatProgressBarModule,
    FormsModule,
    MatButtonToggleModule,
  ],
  templateUrl: './rag-vs-agentic-widget.component.html',
  styleUrls: ['./rag-vs-agentic-widget.component.scss'],
})
export class RagVsAgenticWidgetComponent implements OnChanges {
  @Input() trendData: RagVsAgenticTrendData | null = null;
  @Input() isLoadingTrend = false;
  @Input() trendProgress = 0;
  @Input() trendProgressMessage = '';
  @Input() trendStartDate: Date | null = null;
  @Input() trendEndDate: Date | null = null;

  @Input() snapshotData: RagVsAgenticComparisonData | null = null;
  @Input() isLoadingSnapshot = false;

  @Output() trendFilter = new EventEmitter<void>();
  @Output() trendClear = new EventEmitter<void>();
  @Output() trendStartDateChange = new EventEmitter<Date | null>();
  @Output() trendEndDateChange = new EventEmitter<Date | null>();

  activeView: 'trend' | 'snapshot' = 'snapshot';
  readonly ratingKeys = ['5', '4', '3', '2', '1'] as const;

  aggregatedEntry: RagVsAgenticEntry | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['snapshotData']) {
      this.aggregatedEntry = this.buildAggregatedEntry();
    }
  }

  /** Aggregate all daily entries into one summary covering the full selected range. */
  private buildAggregatedEntry(): RagVsAgenticEntry | null {
    const entries = this.snapshotData?.daily;
    if (!entries || entries.length === 0) return null;

    const ragTotal = entries.reduce((s, e) => s + e.rag.total, 0);
    const agentTotal = entries.reduce((s, e) => s + e.agentLlm.total, 0);

    const ragWithRatings = entries.reduce((s, e) => s + e.rag.withRatings, 0);
    const agentWithRatings = entries.reduce(
      (s, e) => s + e.agentLlm.withRatings,
      0
    );

    const ragAvgRating =
      ragWithRatings > 0
        ? entries.reduce(
            (s, e) => s + e.rag.averageRating * e.rag.withRatings,
            0
          ) / ragWithRatings
        : 0;
    const agentAvgRating =
      agentWithRatings > 0
        ? entries.reduce(
            (s, e) => s + e.agentLlm.averageRating * e.agentLlm.withRatings,
            0
          ) / agentWithRatings
        : 0;

    const ragCoverage = ragTotal > 0 ? (ragWithRatings / ragTotal) * 100 : 0;
    const agentCoverage =
      agentTotal > 0 ? (agentWithRatings / agentTotal) * 100 : 0;

    const ragDist = this.sumDistributions(entries.map((e) => e.rag));
    const agentDist = this.sumDistributions(entries.map((e) => e.agentLlm));

    const avgRatingDiff = agentAvgRating - ragAvgRating;
    const avgRatingDiffPct =
      ragAvgRating > 0 ? (avgRatingDiff / ragAvgRating) * 100 : 0;

    const ragRating5 = entries.reduce(
      (s, e) => s + e.rag.ratingDistribution['5'].count,
      0
    );
    const agentRating5 = entries.reduce(
      (s, e) => s + e.agentLlm.ratingDistribution['5'].count,
      0
    );
    const rating5Diff = agentRating5 - ragRating5;
    const rating5DiffPct =
      ragRating5 > 0 ? (rating5Diff / ragRating5) * 100 : 0;

    const sortedEntries = [...entries].sort((a, b) => a.sk.localeCompare(b.sk));
    const earliest = sortedEntries[0];
    const latest = sortedEntries[sortedEntries.length - 1];

    return {
      dateRange: {
        startDate: this.snapshotData!.dateRange.startDate,
        endDate: this.snapshotData!.dateRange.endDate,
      },
      rag: {
        total: ragTotal,
        averageRating: ragAvgRating,
        ratingCoverage: ragCoverage,
        withRatings: ragWithRatings,
        ratingDistribution: ragDist,
        guardrailsBlocked: entries.reduce(
          (s, e) => s + e.rag.guardrailsBlocked,
          0
        ),
        guardrailsBlockedPercentage: 0,
        dataRange: {
          earliest: earliest.dateRange.startDate,
          latest: latest.dateRange.endDate,
        },
        rating4: ragDist['4'],
        rating5: ragDist['5'],
      } as SolutionMetrics,
      agentLlm: {
        total: agentTotal,
        averageRating: agentAvgRating,
        ratingCoverage: agentCoverage,
        withRatings: agentWithRatings,
        ratingDistribution: agentDist,
        guardrailsBlocked: entries.reduce(
          (s, e) => s + e.agentLlm.guardrailsBlocked,
          0
        ),
        guardrailsBlockedPercentage: 0,
        dataRange: {
          earliest: earliest.dateRange.startDate,
          latest: latest.dateRange.endDate,
        },
        rating4: agentDist['4'],
        rating5: agentDist['5'],
      } as SolutionMetrics,
      comparison: {
        avgRatingDifference: avgRatingDiff,
        avgRatingDifferencePercentage: avgRatingDiffPct,
        rating5Difference: rating5Diff,
        rating5DifferencePercentage: rating5DiffPct,
        rating4Difference: 0,
        rating4DifferencePercentage: 0,
      } as ComparisonMetrics,
      pk: 'aggregated',
      sk: 'aggregated',
    };
  }

  private sumDistributions(solutions: SolutionMetrics[]): RatingDistribution {
    const keys = ['1', '2', '3', '4', '5'] as const;
    const totalOverall = solutions.reduce((s, m) => s + m.total, 0);
    const dist: RatingDistribution = {
      '1': { count: 0, percentage: 0 },
      '2': { count: 0, percentage: 0 },
      '3': { count: 0, percentage: 0 },
      '4': { count: 0, percentage: 0 },
      '5': { count: 0, percentage: 0 },
    };
    for (const k of keys) {
      const count = solutions.reduce(
        (s, m) => s + m.ratingDistribution[k].count,
        0
      );
      dist[k] = {
        count,
        percentage: totalOverall > 0 ? (count / totalOverall) * 100 : 0,
      };
    }
    return dist;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatDateRange(startDate: string, endDate: string): string {
    return `${this.formatDate(startDate)} – ${this.formatDate(endDate)}`;
  }

  formatRating(rating: number): string {
    return rating > 0 ? rating.toFixed(2) : '–';
  }

  formatPercentage(num: number): string {
    return num > 0 ? num.toFixed(1) + '%' : '0%';
  }

  formatDiff(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}`;
  }

  formatDiffPercentage(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  isPositiveDiff(value: number): boolean {
    return value > 0;
  }

  isNegativeDiff(value: number): boolean {
    return value < 0;
  }

  getTotalRatingsFromDistribution(metrics: SolutionMetrics): number {
    const dist = metrics.ratingDistribution;
    return (
      dist['1'].count +
      dist['2'].count +
      dist['3'].count +
      dist['4'].count +
      dist['5'].count
    );
  }

  getRecalculatedCoverage(metrics: SolutionMetrics): number {
    const totalRatings = this.getTotalRatingsFromDistribution(metrics);
    return metrics.total > 0 ? (totalRatings / metrics.total) * 100 : 0;
  }
}
